import React from 'react';
import PropTypes from 'prop-types';
import _values from 'lodash/values';

/**
* Usage:
*

const Input = InputValidator(require('./Input'));

<Form onValidSubmit={this.onValidSubmit}>
    <Input name="some-input" type="text" validations="isLength:4:12" validationErrorMsg="You are wrong."/>
</Form>
*
* onValidSubmit will only be called if the form passes validation
*/

class Form extends React.Component {
    // input holds the component given a "name" prop
    // model holds the state (form data)

    constructor() {
        super();
        this.inputs = {};
        this.model = {};
    }

    connectValidator(component) {
        this.inputs[component.props.name] = component;
        this.model[component.props.name] = component.state.value;
    }

    // clean up model
    disconnectValidator(component) {
        delete this.inputs[component.props.name];
        delete this.model[component.props.name];
    }

    cloneChildrenWithValidations(children) {
        // hit bottom
        if (typeof children !== 'object' || children === null) {
            return children;
        }

        return React.Children.map(children, (child) => {
            if (typeof child !== 'object' || child === null) {
                return child;
            }

            // input element. About to get validated.
            if (child.props && child.props.name) {
                // Clone and return a new ReactElement using element as the starting point.
                // The resulting element will have the original element's props
                // with the new props merged in shallowly. New children will replace
                // existing children.
                return React.cloneElement(
                    child,
                    {
                        connectValidator: this.connectValidator.bind(this),
                        disconnectValidator:
                            this.disconnectValidator.bind(this),
                    },
                    child.props && child.props.children
                );
            } else {
                return React.cloneElement(
                    child,
                    {},
                    this.cloneChildrenWithValidations(
                        child.props && child.props.children
                    )
                );
            }
        });
    }

    // Go through all the models and update their state values
    updateModel() {
        Object.keys(this.inputs).forEach((name) => {
            this.model[name] = this.inputs[name].state.value;
        });
    }

    runValidations() {
        const validatableFields = _values(this.inputs);
        return validatableFields.length > 0
            ? validatableFields.every((f) => f.validate())
            : true;
    }

    // if validations succeeds, update model and call parent submit
    onSubmit(event) {
        event.preventDefault();

        if (this.runValidations()) {
            this.updateModel();
            this.props.onValidSubmit(this.model);
        }
    }

    reset() {
        this.form.reset();
    }

    render() {
        return (
            <form
                className={this.props.className}
                noValidate
                onSubmit={this.onSubmit.bind(this)}
                ref={(ref) => {
                    this.form = ref;
                }}
            >
                {this.cloneChildrenWithValidations(this.props.children)}
            </form>
        );
    }
}

Form.propTypes = {
    onValidSubmit: PropTypes.func,
    className: PropTypes.string,
};

Form.defaultProps = {
    onValidSubmit() {},
};

export default Form;
